import React, { useState, useEffect, useCallback } from "react";
import { Star,Package } from "lucide-react";
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../Admin/Config/Config';
import { useNavigate } from 'react-router-dom';

export default function Categories() {
  const navigate = useNavigate();
  const [categoryList, setCategoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch categories from Firebase
  const fetchCategories = useCallback(async () => {
    try {
      setIsLoading(true);
      const categoryQuery = query(
        collection(db, "category"),
        where("status", "==", true),
        orderBy("order", "asc")
      );
      
      const querySnapshot = await getDocs(categoryQuery);
      const categories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setCategoryList(categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Fallback to static data with plastic container images
      setCategoryList([
        {
          id: 'packaging',
          category_name: 'Packaging Supplies',
          image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=400&fit=crop&crop=center',
          color: 'from-red-500 to-red-600',
          bgColor: 'bg-red-50',
          textColor: 'text-red-600',
          popular: true
        },
        {
          id: 'containers',
          category_name: 'Plastic Containers',
          image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop&crop=center',
          color: 'from-blue-500 to-blue-600',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-600'
        },
        {
          id: 'storage',
          category_name: 'Storage Boxes',
          image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=400&fit=crop&crop=center',
          color: 'from-green-500 to-green-600',
          bgColor: 'bg-green-50',
          textColor: 'text-green-600'
        },
        {
          id: 'moving',
          category_name: 'Moving Supplies',
          image: 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=400&h=400&fit=crop&crop=center',
          color: 'from-purple-500 to-purple-600',
          bgColor: 'bg-purple-50',
          textColor: 'text-purple-600'
        },
        {
          id: 'industrial',
          category_name: 'Industrial Containers',
          image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&h=400&fit=crop&crop=center',
          color: 'from-orange-500 to-orange-600',
          bgColor: 'bg-orange-50',
          textColor: 'text-orange-600'
        },
        {
          id: 'custom',
          category_name: 'Custom Solutions',
          image: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=400&h=400&fit=crop&crop=center',
          color: 'from-gray-500 to-gray-600',
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-600'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Ensure the page loads from the top when this component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCategoryNavigation = useCallback((categoryName) => {
    const encodedCategory = encodeURIComponent(categoryName);
    navigate(`/search-items?query=${encodedCategory}&f=${categoryName}`);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Modern Mobile Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl px-6 py-5 md:hidden">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 bg-clip-text text-transparent">
              Categories
            </h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">Discover our collection</p>
          </div>
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/25">
            <Package className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      {/* Modern Desktop Header */}
      <div className="hidden md:block text-center py-6 px-6">
        <div className="max-w-4xl mx-auto">
          {/* <h1 className="text-6xl font-bold bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 bg-clip-text text-transparent mb-6 leading-tight">
            Our Categories
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Discover our comprehensive range of premium packaging and moving solutions designed for every need
          </p> */}
          {/* <div className="flex items-center justify-center space-x-8 mt-8">
            <div className="flex items-center space-x-2 text-slate-600">
              <Truck className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-medium">Fast Delivery</span>
            </div>
            <div className="flex items-center space-x-2 text-slate-600">
              <Award className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium">Premium Quality</span>
            </div>
            <div className="flex items-center space-x-2 text-slate-600">
              <Shield className="w-5 h-5 text-purple-500" />
              <span className="text-sm font-medium">Secure Packaging</span>
            </div>
          </div> */}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-20">
        {/* Modern Loading State */}
        {isLoading ? (
          <>
            {/* Mobile Loading */}
            <div className="md:hidden grid grid-cols-2 gap-4 px-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-white rounded-3xl p-4 shadow-sm border border-gray-100">
                  <div className="w-full aspect-square mb-4">
                    <div className="bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl w-full h-full animate-pulse"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded-full w-full animate-pulse"></div>
                    <div className="h-3 bg-gray-200 rounded-full w-2/3 animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Desktop Loading */}
            <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
                  <div className="w-20 h-20 bg-gray-200 rounded-3xl mb-6 animate-pulse"></div>
                  <div className="space-y-4">
                    <div className="h-6 bg-gray-200 rounded-full w-3/4 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded-full w-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded-full w-2/3 animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Modern Mobile Categories Grid - 2 Column Layout */
          <div className="md:hidden grid grid-cols-2 gap-4 ">
            {categoryList.map((category, index) => (
              <div
                key={category.id}
                className="bg-white rounded-3xl p-2  border border-gray-200 hover:border-gray-300 transition-all duration-500 group cursor-pointer hover:scale-[1.02] relative overflow-hidden"
                onClick={() => handleCategoryNavigation(category.category_name)}
              >
                
                {/* Popular Badge */}
                {category.popular && (
                  <div className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs px-3 py-1.5 rounded-full font-bold flex items-center space-x-1 z-20">
                    <Star className="w-3 h-3 fill-current" />
                    <span>Hot</span>
                  </div>
                )}
                
                <div className="relative z-10">
                  {/* Image Container */}
                  <div className="w-3/4 aspect-square mb-4 relative mx-auto">
                    <div className="w-full h-full rounded-2xl overflow-hidden ">
                      <img
                        src={category.image}
                        alt={category.category_name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Category Info */}
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-slate-800 leading-tight">
                      {category.category_name}
                    </h3>
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modern Desktop Grid Layout */}
        <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 px-4">
          {categoryList.map((category, index) => (
            <div
              key={category.id}
              className="bg-white rounded-3xl p-8 border border-gray-200 hover:border-gray-300 transition-all duration-500 overflow-hidden group cursor-pointer hover:scale-[1.02] relative"
            >
              {/* Background Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${category.color || 'from-red-500 to-red-600'} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
              
              {/* Popular Badge */}
              {category.popular && (
                <div className="absolute top-6 right-6 bg-gradient-to-r from-red-500 to-red-600 text-white text-sm px-4 py-2 rounded-full font-bold flex items-center space-x-2 z-20">
                  <Star className="w-4 h-4 fill-current" />
                  <span>Popular</span>
                </div>
              )}

              <div  onClick={() => handleCategoryNavigation(category.category_name)} className="relative z-10">
                {/* Enhanced Image Container */}
                <div className="w-24 h-24  rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 group-hover:rotate-3 transition-all duration-700 overflow-hidden border border-gray-200">
                  <img
                    src={category.image}
                    alt={category.category_name}
                    className="w-full h-full object-cover rounded-3xl"
                    loading="lazy"
                  />
                </div>

                {/* Content */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-3 group-hover:text-slate-900 transition-colors duration-300">
                      {category.category_name}
                    </h3>
                    {/* <p className="text-slate-600 leading-relaxed">
                      Discover our premium {category.category_name.toLowerCase()} collection with guaranteed quality
                    </p> */}
                  </div>

                  {/* Features */}
                  {/* <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <Zap className="w-4 h-4 text-yellow-600" />
                      </div>
                      <span className="text-sm text-slate-600 font-medium">Fast Delivery</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <Shield className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-sm text-slate-600 font-medium">Premium Quality</span>
                    </div>
                  </div> */}

                  {/* CTA Button */}
                  {/* <button
                    onClick={() => handleCategoryNavigation(category.category_name)}
                    className={`w-full px-6 py-4 bg-gradient-to-r ${category.color || 'from-red-500 to-red-600'} text-white rounded-2xl font-bold transition-all duration-300 flex items-center justify-center space-x-3 group/btn`}
                  >
                    <span>Explore Collection</span>
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </button> */}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}